import { useEssentials } from '../../../hooks/utils/useEssentials'
import { useSalesHourly } from '../../../hooks/sales'
import { Constants } from '../../../constants/constants'
import {
    getGreenfieldFilterValues,
    getGreenfieldLocationFilter,
} from '../../../utils/greenfield'
import { format, isValid, parse } from 'date-fns'
import { Container } from '../../../components/container/Container'
import { BasicPlaceholder } from '../../../components/placeholder'
import { ErrorAlert, ErrorFallback } from '../../../components/error-fallback'
import { Grid } from '@design-systems/canvas-ui'
import { Table } from 'components/table-compat/table-compat'
import { buildDataTableObject, Cell } from '../../../utils/data'
import { ErrorBoundary } from 'react-error-boundary'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { translateTableData } from 'locales/utils'
import useRefresh from '../../../hooks/utils/useRefresh'

export const HourlySalesPage = () => {
    const { t } = useEssentials()
    const { overview } = TRANSLATION_KEYS.sales

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(overview.hourlySales)}
                />
            )}
        >
            <HourlySales />
        </ErrorBoundary>
    )
}

export const HourlySales = () => {
    const { currentLocation, env, session, t } = useEssentials()
    const { hourly, overview } = TRANSLATION_KEYS.sales

    const { data, isLoading, isError, error, refetch, remove } = useSalesHourly(
        {
            env,
            accessToken: session?.accessToken ?? '',
            dynamicSubPath: Constants.GREENFIELD_CARDS.SALES.HOURLY,
            filters: {
                ...getGreenfieldFilterValues(
                    'SALES_DATE',
                    format(new Date(), 'yyyy-MM-dd'),
                ),
                ...getGreenfieldLocationFilter(currentLocation),
            },
            location: currentLocation,
        },
    )

    useRefresh(refetch, remove)

    if (isLoading) {
        return (
            <Container>
                <div
                    className="placeholder-container"
                    data-testid="placeholder"
                >
                    <BasicPlaceholder type="text" rows={1} />
                    <BasicPlaceholder type="table" rows={8} columns={3} />
                </div>
            </Container>
        )
    }

    if (isError) {
        return (
            <Container>
                <ErrorAlert error={error} onRetry={() => refetch()} />
            </Container>
        )
    }
    // Defensive: ensure rows exist before attempting to transform times
    if (data?.rows) {
        for (const row of data.rows) {
            if (typeof row?.[0]?.value === 'string') {
                const parsedTime = parse(row[0].value, 'hh a', new Date())
                if (!isValid(parsedTime)) continue
                row[0].value = format(parsedTime, 'h:mma')
            }
        }
    }

    const translatedHourlyData = data
        ? {
              ...data,
              headings: data.headings?.map((heading: Cell) =>
                  heading.value.toLowerCase() === 'ly ad mapped'
                      ? { ...heading, value: 'lyAdMapped' }
                      : heading,
              ),
          }
        : data

    const translatedData = translateTableData({
        t,
        table: translatedHourlyData,
        translation: hourly,
    })

    return (
        <Container>
            <Grid.Container className="hc8-mb-3x" justify="space-between">
                <Grid.Item
                    size={{
                        xs: 12,
                    }}
                >
                    <Table
                        data={buildDataTableObject({
                            currentData: translatedData,
                            widths: [80, 80, 80, 80, 80, 80, 80, 80, 80, 80],
                            pinFirstColumn: true,
                        })}
                        scrollableWidth
                        name={t(overview.hourlySales)}
                        headers={[
                            t(hourly.hour),
                            t(hourly.today),
                            t(hourly.lyAdMapped),
                            t(hourly.variance),
                            t(hourly.Transaction_count),
                            t(hourly['Basket Size $']),
                            t(hourly['Basket Size U']),
                            t(hourly['Basket $ b/(P) % LY']),
                            t(hourly['Basket Units b/(P) % LY']),
                            t(hourly['Transaction Count b/(P) % LY']),
                        ]}
                    />
                </Grid.Item>
            </Grid.Container>
        </Container>
    )
}
